import { useEffect, useState } from 'react';
import { CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { licenseApi, type LicenseStatus } from '@/services/api/license';
import { useAuthStore } from '@/stores/auth';

export function Billing() {
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setLicenseStatus } = useAuthStore();

  async function refresh() {
    try {
      setLoading(true);
      const s = await licenseApi.verify();
      setStatus(s);
      setLicenseStatus(s.status);
    } catch (e: any) {
      setError(e?.message ?? '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleCheckout() {
    setPurchasing(true);
    setError(null);
    try {
      const res = await licenseApi.checkout(
        `${window.location.origin}/billing?success=1`,
        `${window.location.origin}/billing?cancel=1`,
      );
      window.location.href = res.checkout_url;
    } catch (e: any) {
      setError(
        e?.response?.status === 503
          ? 'KOMOJU 決済が未設定です。(KOMOJU_SECRET_KEY 未設定)'
          : (e?.message ?? '決済開始に失敗しました'),
      );
    } finally {
      setPurchasing(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-6 flex items-center gap-2 text-slate-400 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> 読み込み中…
      </div>
    );
  }

  const active = status?.status === 'active';

  return (
    <div className="px-4 py-4 max-w-md mx-auto">
      <h1 className="text-xl font-semibold mb-1">ライセンス</h1>
      <p className="text-xs text-slate-400 mb-4">買い切り型のライフタイムライセンス</p>

      <div className="card mb-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] text-slate-500 mb-1">現在のステータス</div>
            <div className="text-base font-medium">
              {active ? (
                <span className="text-emerald-400 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> アクティブ
                </span>
              ) : (
                <span className="text-slate-400">未購入</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-slate-500">デバイス</div>
            <div className="text-sm">
              {status?.device_slot.used}/{status?.device_slot.max}
            </div>
          </div>
        </div>
        {status?.license_key && (
          <div className="mt-3 pt-3 border-t border-slate-800">
            <div className="text-[11px] text-slate-500 mb-1">ライセンスキー</div>
            <code className="text-xs text-slate-300 break-all">{status.license_key}</code>
          </div>
        )}
      </div>

      {!active && (
        <div className="card">
          <h2 className="text-base font-medium mb-1">ライフタイムライセンス</h2>
          <div className="text-3xl font-bold mb-1">¥24,800</div>
          <div className="text-xs text-slate-500 mb-4">税込・買い切り</div>
          <ul className="text-sm text-slate-300 space-y-1.5 mb-5">
            <li>✓ 最大3デバイスで利用可能</li>
            <li>✓ 無制限のプロジェクト作成</li>
            <li>✓ 将来のアップデート無料</li>
            <li>✓ BYOK(自分のAPIキー使用)</li>
          </ul>
          {error && <div className="text-sm text-red-400 mb-3">{error}</div>}
          <button
            onClick={handleCheckout}
            disabled={purchasing}
            className="btn-primary w-full"
          >
            {purchasing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            KOMOJU で購入
          </button>
        </div>
      )}
    </div>
  );
}
